import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import CustomTable from "../components/CustomTable";
import AddCustomerPopup from "./components/AddCustomerPopup";
import { formatDate } from "../../../utils/DateFormat";
import SearchAndFilterBar from "../components/SearchAndFilterBar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthContext";
import { customerService } from "../../../services/api/customerService";
import Loading from "../components/Loading";
import { toast } from "react-toastify";
import { StatusBadge } from "../../../constants/statusConstants";
import Modal from "../components/Modal";

const parseDDMMYYYY = (ddmmyyyy) => {
  if (!ddmmyyyy) return null;
  const [dd, mm, yyyy] = ddmmyyyy.split("-");
  const d = Number(dd), m = Number(mm), y = Number(yyyy);
  if (!d || !m || !y) return null;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() + 1 !== m || date.getDate() !== d) return null;
  return date;
};

const ITEMS_PER_PAGE = 10;

const ManageCustomer = () => {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  const [tableLoading, setTableLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const canDelete = user?.role === "SuperAdmin";
  const [isPopupVisible, setPopupVisible] = useState(false);

  const [rows, setRows] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [totalPages, setTotalPages] = useState(1);

  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    customerId: null,
    customerName: "",
  });

  const [editingData, setEditingData] = useState(null);
  const [isEditPopupVisible, setEditPopupVisible] = useState(false);
  const [isFetchingDetail, setIsFetchingDetail] = useState(false);

  const handleEditCustomer = async (accountId) => {
    try {
      setIsFetchingDetail(true);
      const res = await customerService.get_by_id(accountId, token);
      setEditingData(res.data);
      setEditPopupVisible(true);
    } catch (err) {
      toast.error("Không thể lấy thông tin chi tiết khách hàng");
    } finally {
      setIsFetchingDetail(false);
    }
  };

  const currentPage = Number(searchParams.get("page")) || 1;
  const urlSearch = searchParams.get("search") || "";
  const urlFromDate = searchParams.get("fromDate") || "";
  const urlToDate = searchParams.get("toDate") || "";

  const [search, setSearch] = useState(urlSearch);
  const [fromDate, setFromDate] = useState(urlFromDate);
  const [toDate, setToDate] = useState(urlToDate);

  useEffect(() => { setSearch(urlSearch); }, [urlSearch]);
  useEffect(() => { setFromDate(urlFromDate); }, [urlFromDate]);
  useEffect(() => { setToDate(urlToDate); }, [urlToDate]);

  const transformData = useCallback((list, page, pageSize) => {
    if (!Array.isArray(list)) return [];
    return list.map((item, index) => ({
      stt: pageSize * (page - 1) + index + 1,
      accountId: item.accountId,
      makh: item.staffCode || "--",
      khachhang: {
        companyName: item.fullName || item.companyName || "--",
        companyImage: item.avatar?.url || "/images/avatar-default.png",
        phone: item.phone || item.MST || "--",
      },
      department: item.responsibleDepartmentId?.name || "--",
      assignedStaff: item.assignedStaff || "--",
      account: item.email || "--",
      joinDate: item.joinDate ? formatDate(item.joinDate) : "--",
      trangThai: item.status || "--",
      nav_id: item.accountId,
    }));
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      setTableLoading(true);
      const res = await customerService.search_list(
        currentPage, itemsPerPage, urlSearch, urlFromDate, urlToDate, token,
      );
      setRows(transformData(res.data, currentPage, itemsPerPage));
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      toast.dismiss();
      toast.error("Lỗi lấy danh sách khách hàng");
    } finally {
      setTableLoading(false);
    }
  }, [currentPage, itemsPerPage, urlSearch, urlFromDate, urlToDate, token, transformData]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const applyFiltersToUrl = useCallback((next = {}) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("page", (next.page ?? 1).toString());
      if (next.search !== undefined) { next.search ? p.set("search", next.search) : p.delete("search"); }
      if (next.fromDate !== undefined) { next.fromDate ? p.set("fromDate", next.fromDate) : p.delete("fromDate"); }
      if (next.toDate !== undefined) { next.toDate ? p.set("toDate", next.toDate) : p.delete("toDate"); }
      return p;
    });
  }, [setSearchParams]);

  const handleSearchClick = () => applyFiltersToUrl({ search, fromDate, toDate, page: 1 });
  const onSearchChange = (e) => setSearch(e.target.value);

  const handleFromDateChange = (newFrom) => {
    if (!newFrom) { setFromDate(""); return; }
    const from = parseDDMMYYYY(newFrom);
    const to = parseDDMMYYYY(toDate);
    if (!from) { toast.error("Ngày bắt đầu không hợp lệ (dd-mm-yyyy)"); return; }
    if (to && from > to) { toast.error("Ngày bắt đầu không được lớn hơn ngày kết thúc."); return; }
    setFromDate(newFrom);
  };

  const handleToDateChange = (newTo) => {
    if (!newTo) { setToDate(""); return; }
    const to = parseDDMMYYYY(newTo);
    const from = parseDDMMYYYY(fromDate);
    if (!to) { toast.error("Ngày kết thúc không hợp lệ (dd-mm-yyyy)"); return; }
    if (from && to < from) { toast.error("Ngày kết thúc không được nhỏ hơn ngày bắt đầu."); return; }
    setToDate(newTo);
  };

  const handleDeleteClick = (id) => {
    const customer = rows.find((row) => row.accountId === id);
    setDeleteModal({
      isOpen: true,
      customerId: id,
      customerName: customer?.khachhang?.companyName || "khách hàng này",
    });
  };

  const handleDeleteConfirm = async () => {
    const { customerId } = deleteModal;
    if (!customerId) return;
    try {
      setDeleteLoading(true);
      closeDeleteModal();
      await customerService.delete(customerId, token);
      toast.success("Xóa khách hàng thành công!");
      fetchCustomers();
    } catch (err) {
      toast.dismiss();
      toast.error("Lỗi khi xóa khách hàng");
    } finally {
      setDeleteLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, customerId: null, customerName: "" });
  };

  const columns = useMemo(() => [
    { key: "stt", title: "", align: "text-center" },
    {
      key: "makh",
      title: "Mã KH",
      render: (value) => <div className="text-[#0267FF] no-underline">{value}</div>,
    },
    {
      key: "khachhang",
      title: "Khách hàng",
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="flex flex-col truncate">
            <p className="truncate">{value.companyName}</p>
            <p className="text-gray-500 text-sm">{value.phone}</p>
          </div>
        </div>
      ),
    },
    {
      key: "department",
      title: "Phòng ban phụ trách",
      render: (value) =>
        value !== "--" ? (
          <span className="px-2 py-1 rounded-md text-xs bg-blue-100 text-blue-700">{value}</span>
        ) : ("--"),
    },
    {
      key: "assignedStaff",
      title: "Nhân viên phụ trách",
      render: (value) =>
        value && value !== "--" && value.fullName ? (
          <span className="px-2 py-1 rounded-md text-xs bg-green-100 text-green-700">{value.fullName}</span>
        ) : ("--"),
    },
    { key: "account", title: "Tài khoản" },
    { key: "joinDate", title: "Ngày tham gia" },
    {
      key: "trangThai",
      title: "Trạng thái",
      render: (value) => <StatusBadge status={value} className="min-w-[120px]" />,
    },
  ], []);

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex-shrink-0">
        <SearchAndFilterBar
          searchPlaceholder="Tìm kiếm khách hàng"
          searchValue={search}
          onSearchChange={onSearchChange}
          onSearchClick={handleSearchClick}
          showDatePickers
          showFilter
          filterLabel="Áp dụng"
          fromDatePlaceholder="Từ ngày"
          fromDateValue={fromDate}
          toDatePlaceholder="Đến ngày"
          toDateValue={toDate}
          onFromDateChange={handleFromDateChange}
          onToDateChange={handleToDateChange}
          onAddClick={() => setPopupVisible(true)}
          addButtonLabel="Thêm khách hàng"
          addButtonIcon="/icons/user-plus.svg"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <CustomTable
          data={rows}
          columns={columns}
          currentPage={currentPage}
          setCurrentPage={(page) =>
            setSearchParams((prev) => {
              const p = new URLSearchParams(prev);
              p.set("page", page.toString());
              return p;
            })
          }
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          onRowClick={(item) => {
            navigate(`/quan-ly-khach-hang/khach-hang/${item.accountId}`);
          }}
          setView
          setEdit={true}
          handleEdit={(id, item) => handleEditCustomer(item.accountId)}
          setDelete={canDelete}
          handleDelete={handleDeleteClick}
          basePath="/quan-ly-khach-hang/khach-hang"
          loading={tableLoading}
          loadingText="Đang tải danh sách khách hàng..."
        />
      </div>

      {isFetchingDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg px-6 py-4 flex items-center gap-3 shadow-xl">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="font-medium text-gray-700">Đang tải thông tin...</span>
          </div>
        </div>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        title="Xác nhận xóa"
        maxWidth="500px"
        primaryButtonText="Xóa"
        primaryButtonVariant="danger"
        primaryButtonOnClick={handleDeleteConfirm}
        cancelButtonText="Hủy"
      >
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600">
            Bạn có chắc chắn muốn xóa khách hàng{" "}
            <span className="font-semibold">{deleteModal.customerName}</span>{" "}
            không?
          </p>
          <p className="text-sm text-gray-500 mt-2">Hành động này không thể hoàn tác.</p>
        </div>
      </Modal>

      <AddCustomerPopup
        isVisible={isPopupVisible}
        mode="create"
        onClose={() => setPopupVisible(false)}
        onSubmit={() => {
          setPopupVisible(false);
          fetchCustomers();
        }}
      />

      {isEditPopupVisible && (
        <AddCustomerPopup
          isVisible={isEditPopupVisible}
          mode="edit"
          data={editingData}
          onClose={() => {
            setEditPopupVisible(false);
            setEditingData(null);
          }}
          onSubmit={() => {
            setEditPopupVisible(false);
            setEditingData(null);
            fetchCustomers();
          }}
        />
      )}
    </div>
  );
};

export default ManageCustomer;